const UPDATE_CHECK_INTERVAL = 15 * 60 * 1000;
const UPDATE_ACTIVATION_TIMEOUT = 15000;

export interface AppUpdateState {
  available: boolean;
  updating: boolean;
  activeUpdate: boolean;
  error: string | null;
}

export const initialAppUpdateState: AppUpdateState = {
  available: false,
  updating: false,
  activeUpdate: false,
  error: null,
};

interface AppUpdateOptions {
  workerUrl: string;
  onChange: (state: AppUpdateState) => void;
  onRegistrationError?: (error: unknown) => void;
}

/** Register once per mounted app and reload only after this tab's own consent. */
export function startAppUpdates({ workerUrl, onChange, onRegistrationError }: AppUpdateOptions) {
  const browser = window;
  let state = { ...initialAppUpdateState };
  let disposed = false;
  let registration: ServiceWorkerRegistration | undefined;
  let registering = false;
  let stopChecks: (() => void) | undefined;
  let waitingWorker: ServiceWorker | null = null;
  let updateKnown = false;
  let accepted = false;
  let reloading = false;
  let timeout: number | undefined;
  const workerListeners = new Map<ServiceWorker, () => void>();
  const container = navigator.serviceWorker;
  let controller = container?.controller ?? null;

  const publish = (next: Partial<AppUpdateState>) => {
    if (disposed) return;
    state = { ...state, ...next };
    onChange(state);
  };
  const clearActivationTimeout = () => {
    browser.clearTimeout(timeout);
    timeout = undefined;
  };
  const reload = () => {
    if (disposed || !accepted || reloading) return;
    reloading = true;
    clearActivationTimeout();
    browser.location.reload();
  };
  const announceWaiting = (worker: ServiceWorker) => {
    if (disposed || waitingWorker === worker) return;
    waitingWorker = worker;
    updateKnown = true;
    publish({ available: true, error: null });
  };
  const controllerChanged = () => {
    const nextController = container?.controller;
    if (disposed || !nextController || nextController === controller) return;
    const isUpdate = controller !== null || updateKnown;
    controller = nextController;
    if (accepted) {
      reload();
    } else if (isUpdate) {
      // Another tab may activate the worker. Keep this document and its work
      // intact; reloading it remains a separate, explicit action in this tab.
      waitingWorker = null;
      updateKnown = true;
      clearActivationTimeout();
      publish({ available: true, activeUpdate: true, updating: false, error: null });
    }
    // First installation can claim its first page without being an app update.
  };
  const observeInstalling = () => {
    const worker = registration?.installing;
    if (!worker || workerListeners.has(worker)) return;
    const isUpdate = Boolean(registration?.active || container?.controller);
    const stateChanged = () => {
      if (disposed) return;
      if (worker.state === 'installed' && isUpdate) announceWaiting(worker);
      if (worker.state === 'redundant' && waitingWorker === worker) {
        waitingWorker = null;
        accepted = false;
        clearActivationTimeout();
        publish({ updating: false, error: 'The update could not finish. Please try again.' });
      }
      if (worker.state === 'activated' || worker.state === 'redundant') {
        worker.removeEventListener('statechange', stateChanged);
        workerListeners.delete(worker);
      }
    };
    workerListeners.set(worker, stateChanged);
    worker.addEventListener('statechange', stateChanged);
    stateChanged();
  };
  const register = async () => {
    if (disposed || !container || registration || registering) return;
    registering = true;
    try {
      const registered = await container.register(workerUrl, { type: 'classic', updateViaCache: 'none' });
      if (disposed) return;
      registration = registered;
      registration.addEventListener('updatefound', observeInstalling);
      if (registration.waiting) announceWaiting(registration.waiting);
      observeInstalling();
      stopChecks = startUpdateChecks(registration);
    } catch (error) {
      if (!disposed) onRegistrationError?.(error);
    } finally {
      registering = false;
    }
  };
  const resume = () => {
    if (disposed) return;
    void register();
    if (registration?.waiting) announceWaiting(registration.waiting);
    controllerChanged();
  };
  const assetLoadFailed = () => {
    // A stale tab may request a lazy chunk removed by a newer deployment.
    // Do not auto-reload or swallow the failure: the app's error UI can still
    // handle it, and the user can explicitly refresh after saving their work.
    publish({
      available: true,
      activeUpdate: true,
      error: 'Some app files could not load. Check your connection, save your work, then reload the app.',
    });
  };
  const applyUpdate = async () => {
    if (disposed || state.updating || !state.available) return;
    accepted = true;
    publish({ updating: true, error: null });
    if (state.activeUpdate) {
      reload();
      return;
    }
    try {
      const worker = registration?.waiting ?? waitingWorker;
      if (!worker || worker.state === 'redundant') {
        throw new Error('No waiting update is available');
      }
      timeout = browser.setTimeout(() => {
        accepted = false;
        publish({ updating: false, error: 'The update could not finish. Please try again.' });
      }, UPDATE_ACTIVATION_TIMEOUT);
      worker.postMessage({ type: 'SKIP_WAITING' });
    } catch {
      accepted = false;
      clearActivationTimeout();
      publish({ updating: false, error: 'The update could not finish. Please try again.' });
    }
  };
  const dismiss = () => {
    if (!state.updating) publish({ available: false, error: null });
  };
  if (container) {
    container.addEventListener('controllerchange', controllerChanged);
    browser.addEventListener('focus', resume);
    browser.addEventListener('online', resume);
    void register();
  }
  browser.addEventListener('vite:preloadError', assetLoadFailed);

  return {
    applyUpdate,
    dismiss,
    stop() {
      disposed = true;
      accepted = false;
      clearActivationTimeout();
      stopChecks?.();
      container?.removeEventListener('controllerchange', controllerChanged);
      registration?.removeEventListener('updatefound', observeInstalling);
      browser.removeEventListener('focus', resume);
      browser.removeEventListener('online', resume);
      browser.removeEventListener('vite:preloadError', assetLoadFailed);
      workerListeners.forEach((listener, worker) => worker.removeEventListener('statechange', listener));
      workerListeners.clear();
    },
  };
}

/** Check long-running sessions without polling while the app is hidden or offline. */
export function startUpdateChecks(registration: ServiceWorkerRegistration): () => void {
  const browser = window;
  const page = document;
  const network = navigator;
  let checking = false;
  let disposed = false;

  const checkForUpdate = async () => {
    if (disposed || checking || page.visibilityState !== 'visible' || !network.onLine || registration.installing) return;
    checking = true;
    try {
      await registration.update();
    } catch {
      // Connectivity can change after the online check. Retry on the next check.
    } finally {
      checking = false;
    }
  };

  const interval = browser.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL);
  browser.addEventListener('focus', checkForUpdate);
  browser.addEventListener('online', checkForUpdate);
  page.addEventListener('visibilitychange', checkForUpdate);

  return () => {
    disposed = true;
    browser.clearInterval(interval);
    browser.removeEventListener('focus', checkForUpdate);
    browser.removeEventListener('online', checkForUpdate);
    page.removeEventListener('visibilitychange', checkForUpdate);
  };
}
